import { type Fill, LiquiditySideMaker, LiquiditySideTaker } from '@/api/spec';
import { TradeExecution } from '@/feature/terminal_tables/trade_history/TradeExecution.ts';
import { OrderType } from '@/feature/order_entry/order_types.ts';
import type { MarketData } from '@/common/MarketData';
import { amountToBig } from '@/utils/bigUtils.ts';
import { generateBlockLogId } from '@/utils/generateBlockLogId.ts';
import { deriveOrderFields, scaleValue } from './scaleOrderFields';

export function mapFillsToTradeHistory(fillHistory: Fill[], markets: ReadonlyMap<number, MarketData>): TradeExecution[] {
  return fillHistory
    .map((fill: Fill): TradeExecution | undefined => {
      const market = markets.get(fill.mkt);
      if (!market) {
        return;
      }

      const { side, isClose, isBuy, assetDecimals, quoteDecimals, assetCurrency } = deriveOrderFields(fill.t, market);

      const scaledPrice = scaleValue(fill.p, quoteDecimals, 0);
      const scaledSize = scaleValue(fill.s, assetDecimals, 0);
      const tradeValue = scaledPrice * scaledSize;
      const fee = amountToBig(fill.f, market.quote?.decimals ?? quoteDecimals);

      return {
        id: generateBlockLogId(fill.at).toString(),
        txHash: fill.at?.txid ?? undefined,
        marketId: fill.mkt,
        dateTimestamp: fill.at.t!,
        type: fill.p ? OrderType.limit : OrderType.market,
        assetCurrency,
        assetDecimals,
        quoteDecimals,
        isClose,
        isBuy,
        side,
        size: scaledSize,
        price: scaledPrice,
        tradeValue,
        fee: fee.toNumber(),
        orderId: fill.oid.toString(),
        liquidity: fill.l === LiquiditySideMaker ? 'maker' : fill.l === LiquiditySideTaker ? 'taker' : undefined,
      };
    })
    .filter((v) => v != null);
}
